import { useState, useEffect, useRef, useCallback } from 'react';

interface SubmitReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    email: string;
    vehiclePurchased: string;
    rating: number;
    review: string;
  }) => Promise<boolean>;
  onSuccess: () => Promise<void>;
}

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const TITLE_ID = 'submit-review-modal-title';

export default function SubmitReviewModal({
  isOpen,
  onClose,
  onSubmit,
  onSuccess,
}: SubmitReviewModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    vehiclePurchased: '',
    rating: 0,
    review: '',
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [ratingError, setRatingError] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Body scroll lock + focus management
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      previousFocusRef.current = document.activeElement as HTMLElement;
      const raf = requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });
      return () => cancelAnimationFrame(raf);
    } else {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Focus trap + Escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS) ?? []
    ).filter(el => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.rating === 0) {
      setRatingError(true);
      return;
    }
    setRatingError(false);
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await onSubmit({
        name: formData.name,
        email: formData.email,
        vehiclePurchased: formData.vehiclePurchased,
        rating: formData.rating,
        review: formData.review,
      });

      setSubmitStatus('success');
      setFormData({ name: '', email: '', vehiclePurchased: '', rating: 0, review: '' });

      onSuccess().catch(() => {});

      setTimeout(() => {
        onClose();
        setSubmitStatus('idle');
      }, 3000);
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop — decorative, aria-hidden */}
      <div
        className="fixed inset-0 z-50 bg-black/50"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={TITLE_ID}
          className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <h2 id={TITLE_ID} className="text-xl font-bold text-gray-900">Write a Review</h2>
            <button
              ref={closeButtonRef}
              onClick={onClose}
              aria-label="Close review modal"
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:ring-offset-2"
            >
              <i className="ri-close-line text-xl text-gray-500" aria-hidden="true"></i>
            </button>
          </div>

          {submitStatus === 'success' ? (
            <div
              role="status"
              aria-live="assertive"
              aria-atomic="true"
              className="p-8 text-center"
            >
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-green-100 rounded-full">
                <i className="ri-check-line text-3xl text-green-600" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Thank You!</h3>
              <p className="text-sm text-gray-600">
                Your review has been submitted and will appear on our page after
                verification. We appreciate your feedback!
              </p>
            </div>
          ) : (
            <form
              id="review-submit-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="p-6 space-y-5"
            >
              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Rating *
                </label>
                <div className="flex gap-1" role="group" aria-label="Star rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                      aria-pressed={formData.rating >= star}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, rating: star }));
                        setRatingError(false);
                      }}
                      className="cursor-pointer p-0.5"
                    >
                      <i
                        className={`${
                          (hoverRating || formData.rating) >= star
                            ? 'ri-star-fill text-amber-400'
                            : 'ri-star-line text-gray-300'
                        } text-2xl`}
                        aria-hidden="true"
                      ></i>
                    </button>
                  ))}
                </div>
                {ratingError && (
                  <p className="text-xs text-red-500 mt-1" role="alert">
                    Please select a rating
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="rev-name" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="rev-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="rev-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="rev-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="rev-vehicle" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Vehicle Purchased
                </label>
                <input
                  type="text"
                  id="rev-vehicle"
                  name="vehiclePurchased"
                  value={formData.vehiclePurchased}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="e.g. 2023 BMW X5"
                />
              </div>

              <div>
                <label htmlFor="rev-text" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Your Review *
                </label>
                <textarea
                  id="rev-text"
                  name="review"
                  value={formData.review}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  maxLength={500}
                  rows={4}
                  aria-describedby="rev-char-count"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent resize-none"
                  placeholder="Tell us about your experience..."
                />
                <p id="rev-char-count" className="text-xs text-gray-400 mt-1" aria-live="polite" aria-atomic="true">
                  {formData.review.length}/500 characters
                </p>
              </div>

              {/* Photo note */}
              <div className="bg-gray-50 rounded-lg p-3 flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-camera-line text-gray-400" aria-hidden="true"></i>
                </div>
                <p className="text-xs text-gray-500">
                  Want to add photos or videos? Submit your review first, then
                  email them to hello@rockhoundauto.com and we&apos;ll attach them.
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 text-white py-3.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>

              {submitStatus === 'error' && (
                <div
                  role="alert"
                  aria-live="assertive"
                  aria-atomic="true"
                  className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm text-center"
                >
                  Sorry, there was an error. Please try again.
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </>
  );
}
